import { z } from 'zod';
import { Enumify } from './app.types';

export const WorkspaceSourceTypes = {
    MANUAL: 'manual',
    GOOGLE: 'google',
    WIDGET: 'widget',
    COMPANY_TEAM: 'company_team',
    VENDOR_REFERRAL: 'vendor_referral',
    ASSIGN_USER: 'assign_user',
    EVENT_MEMBER: 'event_member',
    QUESTIONNAIRE_AUTOMATION: 'questionnaire_automation',
    BROCHURE: 'brochure',
    SELF_ONBOARDING: 'self_onboarding',
    GROUP_WIDGET: 'group_widget',
    GROUP_MEMBER: 'group_member',
    AISLE_SOCIETY: 'aisle_society',
    LOVERLY: 'loverly',
    MAIN_STREET_WEDDINGS: 'main_street_weddings',
    MANUAL_GROUP_LEAD: 'manual_group_lead',
    VENDOR_MOBILE: 'vendor_mobile',
    MALKUT_VENUE_ONBOARDING: 'malkut_venue_onboarding',
    NETWORK_PROJECT: 'network_project',
    WEDDING_SPOT: 'wedding_spot',
    NETWORK_INVITE: 'network_invite',
    PVL_INQUIRY: 'pvl_inquiry',
    FIRST_INVOICE: 'first_invoice',
    RTS_SURVEY: 'rts_survey',
    COMMUNITY_LP: 'community_lp',
    WORKFLOW_AUTOMATION: 'workflow_automation',
    COMMUNITY_WIDGET: 'community_widget',
    ZAPIER: 'zapier',
    CLIENT_BOT: 'client_bot',
    MANUAL_IOS_APP: 'manual_ios_app',
    FACEBOOK_LEAD_ADS: 'facebook_lead_ads',
    CONTACT_CSV: 'contact_csv',
    FIRST_PROPOSAL: 'first_proposal',
    MANUAL_ANDROID_APP: 'manual_android_app',
    DIRECT_LINK: 'direct_link',
    SCHEDULER: 'scheduler',
    HB_CHROME_EXTENSION: 'hb_chrome_extension',
    AGRABA: 'agraba',
    PROMOTION: 'promotion',
    GIFT_CARD: 'gift_card',
    PREVIEW: 'preview',
    ONBOARDING: 'onboarding',
    FLOW_AUTOMATION: 'flow_automation',
    SEND_TEST_TEMPLATE: 'send_test_template',
    SEND_TEST_SETUP_GUIDE: 'send_test_setup_guide',
    SHARED_TEMPLATE: 'shared_template',
    LEAD_FORM: 'public_flow',
    AWARENESS_DRAFT: 'awareness_draft',
    ONBOARDING_IOS_APP: 'onboarding_ios_app',
    ONBOARDING_ANDROID_APP: 'onboarding_android_app',
    LIFECYCLE_MARKETING: 'lifecycle_marketing'
} as const;

//values of WorkspaceSourceType
export type WorkspaceSourceType = Enumify<typeof WorkspaceSourceTypes>;

export const EventTypes = {
    WEDDING: 'wedding',
    CORPORATE: 'corporate',
    SOCIAL: 'social',
    EVENT: 'event',
    PARTY: 'party',
    OTHER: 'other',
    FAMILY: 'family',
    DESIGN: 'design'
} as const;

export type EventType = Enumify<typeof EventTypes>;

export const WorkspaceTabTypes = {
    HOST: 'host',
    VENDOR: 'vendor',
    NEW: 'new'
} as const;

export type WorkspaceTabType = Enumify<typeof WorkspaceTabTypes>;

// The keys have a _ prefix/sufix because this is how they are stored in the database
// Because they used to map to translation keys in Angular
export const PipelineStageTypesToTransKey = {
    _ALL_ACTIVE_: 'all_active',
    _ARCHIVED_: 'archived',
    _CLOSED_: 'closed',
    _COMPLETED_: 'completed',
    _CONTRACT_SIGNED_: 'contract_signed',
    _DELIVERY_: 'delivery',
    _DISCOVERY_: 'discovery',
    _DO_NOT_TRACK_: 'do_not_track',
    _FOLLOW_UP_: 'follow_up',
    _IN_PROGRESS_: 'in_progress',
    _INQUIRY_: 'inquiry',
    _KICK_OFF_: 'kick_off',
    _LOST_: 'lost',
    _MEETING_: 'meeting',
    _NEGOTIATION_: 'negotiation',
    _NEW_: 'new',
    _ONBOARDING_: 'onboarding',
    _OTHER_: 'other',
    _PAID_: 'paid',
    _PAYMENT_RECEIVED_: 'payment_received',
    _PLANNING_: 'planning',
    _PROPOSAL_: 'proposal',
    _PROPOSAL_SENT_: 'proposal_sent',
    _PROPOSAL_SIGNED_: 'proposal_signed',
    _RETAINER_PAID_: 'retainer_paid',
    _SENT_: 'sent',
    _SIGNED_: 'signed',
    _UNTRACKED_PROJECTS_: 'untracked_projects',
    _WON_: 'won'
} as const;

export type PipelineStageType = Enumify<typeof PipelineStageTypesToTransKey>;

export type PipelineAutomationType =
    | 'inquiry'
    | 'followup'
    | 'sent'
    | 'signed'
    | 'paid'
    | 'inprogress'
    // closed type is to support backwards compatibility
    | 'closed'
    // other type is to support backwards compatibility
    | 'other'
    // meeting doesnt exist anymore, but we need to support it for backwards compatibility
    | 'meeting';

export const PipelineStageCategories = {
    LEAD: 'lead',
    OTHER: 'other'
};

export const PipelineStageGroups = {
    OPPORTUNITIES: 'opportunities',
    PROJECTS: 'projects'
} as const;

export type PipelineStageCategory = Enumify<typeof PipelineStageCategories>;

export type PipelineStageGroup = Enumify<typeof PipelineStageGroups>;

export const WorkspaceStatuses = {
    LEAD: 'lead',
    LEAD_ARCHIVED: 'lead_archived',
    CLIENT: 'client',
    CLIENT_ARCHIVED: 'client_archived',
    LEAD_SENT: 'lead_sent'
} as const;

export const EnumToCd = <T extends Record<string, string>>(
    enumObj: T
): { [key in keyof T]: number } =>
    Object.values(enumObj).reduce((acc, key, index) => {
        acc[key as keyof T] = index;
        return acc;
    }, {} as { [key in keyof T]: number });

export type WorkspaceStatus = Enumify<typeof WorkspaceStatuses>;

export const WorkspaceFileStatuses = {
    NEW_WORKSPACE_CREATE: 'new_workspace_create',
    WELCOME_EMAIL_SENT: 'welcome_email_sent',
    WELCOME_BROCHURE_SEEN: 'welcome_brochure_seen',
    QUESTIONNAIRE_SEEN: 'questionnaire_seen',
    QUESTIONNAIRE_ANSWERED: 'questionnaire_answered',
    PROPOSAL_CREATED: 'proposal_created',
    PROPOSAL_SENT: 'proposal_sent',
    PROPOSAL_BROCHURE_SEEN: 'proposal_brochure_seen',
    PROPOSAL_SEEN: 'proposal_seen',
    AGREEMENT_SEEN: 'agreement_seen',
    AGREEMENT_SIGNED: 'agreement_signed',
    PAYMENTS_SEEN: 'payments_seen',
    RETAINER_PAID: 'retainer_paid',
    PAYMENT_DONE: 'payment_done'
} as const;
export type WorkspaceFileStatus = Enumify<typeof WorkspaceFileStatuses>;
export const WorkspaceFileStatusToCd: { [key in WorkspaceFileStatus]: number } =
    Object.values(WorkspaceFileStatuses).reduce((acc, key, index) => {
        acc[key as WorkspaceFileStatus] = index;
        return acc;
    }, {} as { [key in WorkspaceFileStatus]: number });

export const WorkspaceFileTypes = {
    PROPOSAL: 'proposal',
    INVOICE: 'invoice',
    DIRECT_PAYMENT: 'direct_payment',
    QUESTIONNAIRE: 'questionnaire',
    BROCHURE: 'brochure',
    TIMELINE: 'timeline',
    AGREEMENT: 'agreement'
} as const;
export type WorkspaceFileType = Enumify<typeof WorkspaceFileTypes>;
export const WorkspaceFileTypeToCd: { [key in WorkspaceFileType]: number } =
    Object.values(WorkspaceFileTypes).reduce((acc, key, index) => {
        acc[key as WorkspaceFileType] = index;
        return acc;
    }, {} as { [key in WorkspaceFileType]: number });

export const SourceTypes = {
    MANUAL: 'manual',
    GOOGLE: 'google',
    WIDGET: 'widget',
    COMPANY_TEAM: 'company_team',
    VENDOR_REFERRAL: 'vendor_referral',
    ASSIGN_USER: 'assign_user',
    EVENT_MEMBER: 'event_member',
    QUESTIONNAIRE_AUTOMATION: 'questionnaire_automation',
    BROCHURE: 'brochure',
    SELF_ONBOARDING: 'self_onboarding',
    GROUP_WIDGET: 'group_widget',
    GROUP_MEMBER: 'group_member',
    AISLE_SOCIETY: 'aisle_society',
    LOVERLY: 'loverly',
    MAIN_STREET_WEDDINGS: 'main_street_weddings',
    MANUAL_GROUP_LEAD: 'manual_group_lead',
    VENDOR_MOBILE: 'vendor_mobile',
    MALKUT_VENUE_ONBOARDING: 'malkut_venue_onboarding',
    NETWORK_PROJECT: 'network_project',
    WEDDING_SPOT: 'wedding_spot',
    NETWORK_INVITE: 'network_invite',
    PVL_INQUIRY: 'pvl_inquiry',
    FIRST_INVOICE: 'first_invoice',
    RTS_SURVEY: 'rts_survey',
    COMMUNITY_LP: 'community_lp',
    WORKFLOW_AUTOMATION: 'workflow_automation',
    COMMUNITY_WIDGET: 'community_widget',
    ZAPIER: 'zapier',
    CLIENT_BOT: 'client_bot',
    MANUAL_IOS_APP: 'manual_ios_app',
    FACEBOOK_LEAD_ADS: 'facebook_lead_ads',
    CONTACT_CSV: 'contact_csv',
    FIRST_PROPOSAL: 'first_proposal',
    MANUAL_ANDROID_APP: 'manual_android_app',
    DIRECT_LINK: 'direct_link',
    SCHEDULER: 'scheduler',
    HB_CHROME_EXTENSION: 'hb_chrome_extension',
    AGRABA: 'agraba',
    PROMOTION: 'promotion',
    GIFT_CARD: 'gift_card',
    PREVIEW: 'preview',
    ONBOARDING: 'onboarding',
    FLOW_AUTOMATION: 'flow_automation',
    SEND_TEST_TEMPLATE: 'send_test_template',
    SEND_TEST_SETUP_GUIDE: 'send_test_setup_guide',
    SHARED_TEMPLATE: 'shared_template',
    LEAD_FORM: 'public_flow',
    AWARENESS_DRAFT: 'awareness_draft',
    ONBOARDING_IOS_APP: 'onboarding_ios_app',
    ONBOARDING_ANDROID_APP: 'onboarding_android_app',
    LIFECYCLE_MARKETING: 'lifecycle_marketing'
} as const;
export type SourceType = Enumify<typeof SourceTypes>;

export const WorkspaceOnboardingBotStates = {
    INTRO: 'intro',
    BEFORE_INQUIRY: 'before_inquiry',
    INQUIRY_SENT: 'inquiry_sent',
    BROCHURE_SENT: 'brochure_sent',
    BROCHURE_PACKAGE_SELECTED: 'brochure_package_selected',
    WORKSPACE_MESSAGE_SENT: 'workspace_message_sent',
    PROPOSAL_SENT: 'proposal_sent',
    PROPOSAL_PAID: 'proposal_paid',
    USER_POLL_SUBMITTED: 'user_poll_submitted'
} as const;
export type WorkspaceOnboardingBotState = Enumify<
    typeof WorkspaceOnboardingBotStates
>;

export const WorkspaceTypes = {
    GENERAL: 'general',
    TEAM: 'team',
    DESIGN: 'design',
    ALL_VENDORS: 'all_vendors',
    TIMELINE: 'timeline'
} as const;
export type WorkspaceType = Enumify<typeof WorkspaceTypes>;

export const FraudStatuses = {
    VERIFIED: 'verified',
    NEEDS_VERIFICATION: 'needs_verification',
    SUSPICIOUS: 'suspicious',
    CERTAIN_FRAUD: 'certain_fraud',
    ESCALATION: 'escalation',
    WATCHED: 'watched',
    PAYOUTS_DISABLED: 'payouts_disabled'
} as const;
export type FraudStatus = Enumify<typeof FraudStatuses>;

export const EmailTypes = {
    PROPOSAL: 'Proposal',
    INVOICE: 'Invoice',
    PAYMENT_REMINDER: 'PaymentReminder',
    REQUEST_PROPOSAL: 'RequestProposal',
    QUESTIONNAIRE: 'Questionnaire',
    WIDGET: 'Widget',
    GENERAL: 'General',
    VENDOR_REFERRAL: 'VendorReferral',
    LEAD_THANK_YOU: 'LeadThankYou',
    BROCHURE: 'Brochure',
    TIMELINE: 'Timeline',
    BROCHURE_SELECTION: 'BrochureSelection',
    INBOUND: 'Inbound',
    PRE_EVENT_QUESTIONNAIRE: 'PreEventQuestionnaire',
    CLIENT_FILE_NOT_VIEWED: 'ClientFileNotViewed',
    LEAD_EMAIL: 'LeadEmail',
    LEAD_RECORD_EMAIL: 'LeadRecordEmail',
    RESPOND_TO_LEAD: 'RespondToLead',
    AGREEMENT: 'Agreement',
    WS_EMAIL: 'WsEmail',
    ADD_GROUP_MEMBER: 'AddGroupMember',
    IMPORTED_FROM_GMAIL: 'ImportedFromGmail'
} as const;
export type EmailType = Enumify<typeof EmailTypes>;

export const EmailTrackingSources = {
    FEED: 'Feed',
    EMAIL_OPEN: 'EmailOpen',
    EMAIL_WEBHOOK: 'EmailWebhook'
} as const;
export type EmailTrackingSource = Enumify<typeof EmailTrackingSources>;

export const UserTypeDisplayNames = {
    BRIDE: 'Bride',
    GROOM: 'Groom',
    EVENT_PLANNER: 'Event Planner',
    OTHER: 'Other'
} as const;
export type UserTypeDisplayName = Enumify<typeof UserTypeDisplayNames>;

export const WorkspaceRoles = {
    VENDOR: 'vendor',
    HOST: 'host'
} as const;
export type WorkspaceRole = Enumify<typeof WorkspaceRoles>;

export const WorkspaceMemberStatuses = {
    MEMBER_NEW: 'member_new',
    MEMBER_INVITE_SENT: 'member_invite_sent',
    MEMBER_ACTIVE: 'member_active'
} as const;
export type WorkspaceMemberStatus = Enumify<typeof WorkspaceMemberStatuses>;

export const UserTypes = {
    BRIDE: 'bride',
    GROOM: 'groom',
    EVENT_PLANNER: 'event_planner',
    OTHER: 'other'
} as const;
export type UserType = Enumify<typeof UserTypes>;

export const SystemUserTypes = {
    CLIENT: 'client',
    VENDOR: 'vendor',
    BOTH: 'both',
    OTHER: 'other',
    MALKUT: 'malkut',
    ADMIN: 'admin',
    EXTERNAL: 'external'
} as const;
export type SystemUserType = Enumify<typeof SystemUserTypes>;

export const HbUserTypes = {
    DEMO: 'demo',
    ADMIN: 'admin',
    MALKUT: 'malkut',
    HONEYBOOK_INVOICING: 'honeybook_invoicing',
    REGULAR: 'regular',
    HONEYBOOK_PAYING: 'honeybook_paying',
    PRO: 'pro',
    BOOKKEEPER: 'bookkeeper'
} as const;
export type HbUserType = Enumify<typeof HbUserTypes>;

export const Currencies = {
    USD: 'USD',
    CAD: 'CAD',
    ILS: 'ILS'
} as const;
export type Currency = Enumify<typeof Currencies>;

export const PaymentTypes = {
    RECURRING: 'recurring',
    AUTO: 'auto',
    SCHEDULED: 'scheduled'
} as const;
export type PaymentType = Enumify<typeof PaymentTypes>;

const PvlFlowTypes = {
    CLIENT_REQUESTS: 'client_requests',
    ATTACHED_TO_FILES: 'attached_to_files',
    ATTACHED_TO_FEEDS: 'attached_to_feeds',
    CLIENT_FEED_REQUESTS: 'client_feed_requests'
} as const;
export type PvlFlowType = Enumify<typeof PvlFlowTypes>;

export const FlowTypes = {
    BLOCK_TEMPLATE: 'block_template',
    FLOW_TEMPLATE: 'flow_template',
    FLOW_INSTANCE: 'flow_instance',
    LEAD_FORM: 'public_flow'
} as const;
export type FlowType = Enumify<typeof FlowTypes>;

/*
const AutoExpirationStates = z.enum(['active', 'expired', 'stopped']);
const DurationUnitType = z.enum([
    'seconds',
    'minutes',
    'hours',
    'days',
    'weeks',
    'months',
    'years'
]);
const FlowStartExpirationCondition = z.enum(['after_sent', 'is_viewed']);
*/

export const AutoExpirationStates = {
    ACTIVE: 'active',
    EXPIRED: 'expired',
    STOPPED: 'stopped'
} as const;
export type AutoExpirationState = Enumify<typeof AutoExpirationStates>;

export const DurationUnitTypes = {
    SECONDS: 'seconds',
    MINUTES: 'minutes',
    HOURS: 'hours',
    DAYS: 'days',
    WEEKS: 'weeks',
    MONTHS: 'months',
    YEARS: 'years'
} as const;
export type DurationUnitType = Enumify<typeof DurationUnitTypes>;

export const FlowStartExpirationConditions = {
    AFTER_SENT: 'after_sent',
    IS_VIEWED: 'is_viewed'
} as const;
export type FlowStartExpirationCondition = Enumify<
    typeof FlowStartExpirationConditions
>;

export const TemplateSourceTypes = {
    REFERRAL_TRIAL_TEMPLATES: 'referral_trial_templates',
    MALKUT_IMPORT: 'malkut_import',
    IMPORT_GALLERY: 'import_gallery',
    PRE_USER: 'pre_user',
    IMPORT_PUBLIC: 'import_public',
    MANUAL: 'manual',
    DUPLICATE_TEMPLATE: 'duplicate_template',
    FLOW_MIGRATION: 'flow_migration',
    RECENT_INSTANCES: 'recent_instances',
    MY_TEMPLATES: 'my_templates',
    SAVE_NEW_TEMPLATE: 'save_new_template',
    BLOCK_TEMPLATE: 'block_template',
    TIME_TRACKING: 'time_tracking',
    SERVICE_SELECTION_INVOICE: 'service_selection_invoice',
    MY_TEMPLATES_SIMPLIFIED_WIZARD: 'my_templates_simplified_wizard',
    SHARED_TEMPLATE: 'shared_template',
    LEAD_FORMS: 'public_flows',
    IMPORT_LEAD_FORMS_GALLERY: 'import_public_flows_gallery'
} as const;
export type TemplateSourceType = Enumify<typeof TemplateSourceTypes>;

export const FlowTags = {
    CONTRACT_BLOCK: 'contract_block',
    INVOICE_BLOCK: 'invoice_block',
    PAYMENT_SCHEDULE_BLOCK: 'payment_schedule_block',
    SERVICE_SELECTION_BLOCK: 'service_selection_block',
    CONTAINER_BLOCK: 'container_block',
    COMPANY_INFO_BLOCK: 'company_info_block'
} as const;
export type FlowTag = Enumify<typeof FlowTags>;

export const CalendarItemSources = {
    HONEYBOOK: 'honeybook',
    SCHEDULER: 'scheduler',
    CALENDLY: 'calendly',
    PRODUCT_MARKETING: 'product_marketing',
    FLOW: 'flow'
} as const;

export type CalendarItemSource = Enumify<typeof CalendarItemSources>;

// Session - a session config (scheduler) vs Meeting - a meeting (calendar)
export const SessionTypes = {
    IN_PERSON: 'in_person',
    PHONE_CALL: 'phone_call',
    VIDEO_CALL: 'video_call',
    OTHER: 'other'
} as const;

export type SessionType = Enumify<typeof SessionTypes>;

export const MeetingTypes = {
    ...SessionTypes,
    PERSONAL: 'personal'
} as const;

export type MeetingType = Enumify<typeof MeetingTypes>;

export const SessionCallerTypes = {
    ME: 'me',
    CLIENT: 'client'
} as const;

export type SessionCallerType = Enumify<typeof SessionCallerTypes>;

export const VideoConferenceTypes = {
    GOOGLE: 'google',
    ZOOM: 'zoom',
    OTHER: 'other'
} as const;

export type VideoConferenceType = Enumify<typeof VideoConferenceTypes>;

export const SessionWindowTypes = {
    INDEFINITELY: 'indefinitely',
    ROLLING_WINDOW: 'rolling_window',
    FIXED_DATE_RANGE: 'fixed_date_range'
} as const;

export type SessionWindowType = Enumify<typeof SessionWindowTypes>;

export const SessionReminderTypes = {
    // SMS: 'sms',
    EMAIL: 'email',
    SMS_AND_EMAIL: 'sms_and_email'
} as const;

export type SessionReminderType = Enumify<typeof SessionReminderTypes>;

export const CreateFlowTypes = {
    SERVICES: 'services',
    CONTRACT: 'contract',
    CUSTOM: 'custom',
    INVOICE: 'invoice',
    RECURRING_INVOICE: 'recurring_invoice',
    PROPOSAL: 'proposal',
    QUESTIONNAIRE: 'questionnaire',
    SCHEDULER: 'scheduler'
} as const;

export type CreateFlowType = Enumify<typeof CreateFlowTypes>;

export const CreateLegacyFileTypes = {
    BROCHURE: 'brochure',
    AGREEMENT: 'agreement',
    INVOICE: 'invoice',
    PROPOSAL: 'proposal',
    QUESTIONNAIRE: 'questionnaire',
    TIMELINE: 'timelines'
} as const;

export type CreateLegacyFileType = Enumify<typeof CreateLegacyFileTypes>;
export const WorkflowAutomationTypeValues = [
    // migrated from ng; order matters here! on automation update, the server takes a property automation_type_cd which is the ordinal value of the enum in Ruby
    'reminder_event_coming_up',
    'reminder_unsent_file',
    'reminder_file_not_booked',
    'inquiry_reply_thank_you',
    'automation_send_questionnaire',
    'client_payment_reminder',
    'client_file_not_viewed',
    'reminder_file_sent_email_not_viewed',
    'default_file_expiration',
    'show_date_conflicts',
    'add_automated_emails_to_activity',
    'upcoming_payment_reminder',
    'outstanding_payment_reminder',
    'due_payment_reminder',
    'auto_recurring_payment_reminder',
    'client_flow_not_viewed',
    'send_flow_before_project',
    'longer_outstanding_payment_reminder',
    'late_fee_on_outstanding_payments',
    'task_due_email',
    'task_reminder_email',
    'task_reminder_notification',
    'task_completed'
] as const;
export const WorkflowAutomationType = z.enum(WorkflowAutomationTypeValues);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type WorkflowAutomationType = z.infer<typeof WorkflowAutomationType>;
export type WorkflowAutomationExtendedType =
    | WorkflowAutomationType
    | 'inquiry_follow_up';

export const WorkflowAutomationTimeFrames = [
    'one_week',
    'two_weeks',
    'three_weeks',
    'four_weeks',
    'one_month',
    'two_months',
    'one_day',
    'two_days',
    'three_days',
    'four_days',
    'seven_days',
    'ten_days'
] as const;
export const WorkflowAutomationTimeFrame = z.enum(WorkflowAutomationTimeFrames);
export type TWorkflowAutomationTimeFrame = z.infer<
    typeof WorkflowAutomationTimeFrame
>;

export enum PaymentPlanOption {
    FixedPaymentPlan = 'Fixed Payment Plan',
    ClientPaymentPlan = 'Client Payment Plan'
}

export const Methods2FA = {
    sms: 'sms',
    call: 'call',
    email: 'email'
};

export type TMethod2FA = Enumify<typeof Methods2FA>;

export const UserIntentTypes = {
    invoices: 'invoices',
    contracts: 'contracts',
    automations: 'automations',
    leadCapture: 'leadCapture',
    businessManagement: 'businessManagement',
    crm: 'crm',
    scheduling: 'scheduling',
    brochuresAndQuestionnaires: 'brochuresAndQuestionnaires',
    allInOne: 'all_in_one',
    templates: 'templates'
} as const;

export type UserIntentType = Enumify<typeof UserIntentTypes>;

const OnboardingSteps = {
    account: 'account',
    branding: 'branding',
    services: 'services',
    clientSide: 'client_side',
    subscribe: 'subscribe',
    templates: 'templates',
    createProject: 'create_project',
    contactForm: 'contact_form',
    bankInfo: 'bank_info',
    accountSetup: 'account_setup',
    templateGallery: 'template_gallery',
    sendFlowTest: 'send_flow_test',
    clients: 'clients',
    servicesHub: 'services_hub'
} as const;

export type OnboardingStep = Enumify<typeof OnboardingSteps>;

export const AtwsStages = {
    beforeCreatingFirstEvent: 1,
    createdFirstEvent: 2,
    createdSecondEvent: 3
} as const;

export type AtwsStage = Enumify<typeof AtwsStages>;
